/** 项目内管理同书同渠道计划的 Web 链接；计划身份独立，不以项目编号替代。 */
import {
  type AdPlanListItem,
  CreateAdPlanRequestSchema,
  type LandingProjectDto,
} from '@novel-admin/shared';
import { useEffect, useState } from 'react';
import { useAdPlanMutations, useAdPlansPage } from '~/data/queries';
import { Drawer } from '~/ui/Dialog';
import { Pagination } from '~/ui/Pagination';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { AdPlanWebLinksPanel } from '../dialogs/AdPlanWebLinksPanel';

export function LandingProjectWebLinksDrawer({
  project,
  onClose,
}: {
  project: LandingProjectDto;
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AdPlanListItem | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { create } = useAdPlanMutations();
  const plans = useAdPlansPage({
    page,
    pageSize: 20,
    filters: {
      novel_id: project.novel.id,
      media_source: project.media_source,
    },
  });
  useEffect(() => {
    if (!plans.isLoading && !plans.isError) {
      const lastPage = Math.max(1, Math.ceil(plans.total / 20));
      if (page > lastPage) setPage(lastPage);
    }
  }, [plans.isLoading, plans.isError, plans.total, page]);
  const matchesProject = (plan: AdPlanListItem) =>
    plan.novel_id === project.novel.id && plan.media_source === project.media_source;
  function createPlan() {
    const result = CreateAdPlanRequestSchema.safeParse({
      novel_id: project.novel.id,
      media_source: project.media_source,
      name: name.trim(),
    });
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? '请填写投放计划名称');
      return;
    }
    setError(null);
    create.mutate(result.data, {
      onSuccess: (plan) => {
        if (!matchesProject(plan)) {
          setError('返回的投放计划与当前书籍、渠道不一致，请重新打开');
          return;
        }
        setSelected(plan);
        setName('');
        toast('投放计划已创建', { tone: 'pos' });
      },
      onError: (failure) => setError(failure.message),
    });
  }
  return (
    <Drawer title="Web 投放链接" width={820} onClose={onClose}>
      <p className="muted">
        {project.novel.title} · {project.media_source}。选择同书同渠道的投放计划，链接无需生成
        HTML；项目归档不改变链接状态。
      </p>
      {selected && matchesProject(selected) ? (
        <>
          <div className="hstack" style={{ gap: 8, marginBottom: 12 }}>
            <strong>{selected.name}</strong>
            <button type="button" className="btn btn-sm" onClick={() => setSelected(null)}>
              切换投放计划
            </button>
          </div>
          <AdPlanWebLinksPanel
            key={selected.id}
            planId={selected.id}
            locale={project.novel.locale}
          />
        </>
      ) : (
        <>
          {plans.isLoading ? (
            <p>加载投放计划…</p>
          ) : plans.isError ? (
            <p role="alert">投放计划加载失败，请重新打开后重试</p>
          ) : (
            <Table className="tbl">
              <thead>
                <tr>
                  <th>投放计划</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {plans.data.filter(matchesProject).map((plan) => (
                  <tr key={plan.id}>
                    <td>{plan.name}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => setSelected(plan)}
                      >
                        管理 {plan.name} 的链接
                      </button>
                    </td>
                  </tr>
                ))}
                {plans.total === 0 && (
                  <tr>
                    <td colSpan={2}>还没有同书同渠道的投放计划</td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
          {!plans.isError && (
            <Pagination total={plans.total} page={page} pageSize={20} onChange={setPage} />
          )}
          <div className="hstack" style={{ gap: 8, marginTop: 16 }}>
            <input
              className="input"
              aria-label="投放计划名称"
              placeholder="投放计划名称"
              maxLength={100}
              value={name}
              disabled={create.isPending}
              onChange={(event) => setName(event.target.value)}
            />
            <button
              type="button"
              className="btn btn-sm"
              disabled={!name.trim() || create.isPending}
              onClick={createPlan}
            >
              新建投放计划
            </button>
          </div>
          {error && <p role="alert">{error}</p>}
        </>
      )}
    </Drawer>
  );
}
